import { Link } from "react-router-dom";
import { Pencil, Trash2, MapPinned, ExternalLink, Navigation, ClipboardPen } from "lucide-react";
import {
  POINT_TYPE_LIST,
  getPointType,
  getPointImage,
  getLinkedLocation,
  getPointAddress,
  getDirectionsUrl,
  canManagePoint,
} from "./mapPointTypes";

const THEMES = {
  dark: {
    card: "bg-slate-900",
    border: "border-slate-700",
    text: "text-slate-50",
    muted: "text-slate-400",
    placeholder: "bg-slate-800",
    link: "text-slate-300 hover:text-white",
    divider: "border-slate-800",
    heading: "text-slate-200",
  },
  light: {
    card: "bg-white",
    border: "border-slate-200",
    text: "text-slate-900",
    muted: "text-slate-500",
    placeholder: "bg-slate-100",
    link: "text-slate-600 hover:text-slate-900",
    divider: "border-slate-200",
    heading: "text-slate-700",
  },
};

const MapPointCard = ({
  point,
  locationsById,
  selected,
  onFocus,
  canManage,
  onEdit,
  onDelete,
  embed,
  t,
}) => {
  const type = getPointType(point.type);
  const Icon = type.Icon;
  const linked = getLinkedLocation(point, locationsById);
  const image = getPointImage(point, locationsById);
  const address = getPointAddress(point);
  const locationHref = linked ? `/locations/${linked.id}` : null;
  const accent = "inline-flex items-center gap-1 text-amber-500 hover:text-amber-400";

  return (
    <div
      id={`map-point-${point.id}`}
      className={`flex flex-col rounded-lg border overflow-hidden shadow-lg transition ${t.card} ${t.text} ${
        selected ? "border-amber-400 ring-2 ring-amber-400/40" : t.border
      }`}
    >
      <button
        type="button"
        onClick={() => onFocus(point.id)}
        className={`relative block w-full h-36 overflow-hidden group ${t.placeholder}`}
        title="Voir sur la carte"
      >
        {image ? (
          <img
            src={image}
            alt={point.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={44} color={type.color} strokeWidth={1.5} />
          </div>
        )}
      </button>

      <div className="flex flex-col flex-1 p-3 gap-1">
        <h4 className="font-bold leading-tight">{point.name}</h4>
        {address && <p className={`text-sm ${t.muted}`}>{address}</p>}
        {linked?.table && (
          <p className={`text-xs ${t.muted}`}>Table : {linked.table}</p>
        )}
        {point.description && (
          <p className="text-sm mt-1 line-clamp-3">{point.description}</p>
        )}

        <div className="mt-auto pt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold">
          <button type="button" onClick={() => onFocus(point.id)} className={accent}>
            <MapPinned size={14} /> Carte
          </button>
          {locationHref &&
            (embed ? (
              <a
                href={`${window.location.origin}${locationHref}`}
                target="_blank"
                rel="noopener noreferrer"
                className={accent}
              >
                <ClipboardPen size={14} /> Inscription
              </a>
            ) : (
              <Link to={locationHref} className={accent}>
                <ClipboardPen size={14} /> Inscription
              </Link>
            ))}
          {point.website && (
            <a
              href={point.website}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 ${t.link}`}
            >
              <ExternalLink size={14} /> Site web
            </a>
          )}
          <a
            href={getDirectionsUrl(point)}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 ${t.link}`}
          >
            <Navigation size={14} /> Itinéraire
          </a>
        </div>

        {canManage && !embed && (
          <div className={`flex gap-2 pt-3 mt-2 border-t ${t.divider}`}>
            <button
              type="button"
              onClick={() => onEdit(point)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold transition"
            >
              <Pencil size={14} /> Modifier
            </button>
            <button
              type="button"
              onClick={() => onDelete(point)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-red-700 hover:bg-red-600 text-white text-sm font-semibold transition"
            >
              <Trash2 size={14} /> Supprimer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Cards grouped in one section per type, in POINT_TYPE_LIST order
 * (weekly tournaments first). Empty sections are not rendered.
 */
const MapPointList = ({
  points,
  locationsById,
  selectedId,
  onFocus,
  user = null,
  onEdit,
  onDelete,
  embed = false,
  theme = "dark",
}) => {
  const t = THEMES[theme] || THEMES.dark;

  if (points.length === 0) {
    return (
      <p className={`text-center py-10 ${t.muted}`}>
        Aucun lieu à afficher pour le moment.
      </p>
    );
  }

  const sections = POINT_TYPE_LIST.map((type) => ({
    type,
    items: points.filter((p) => getPointType(p.type).key === type.key),
  })).filter((s) => s.items.length > 0);

  return (
    <div className="space-y-8">
      {sections.map(({ type, items }) => {
        const Icon = type.Icon;
        return (
          <section key={type.key} aria-labelledby={`map-section-${type.key}`}>
            <h3
              id={`map-section-${type.key}`}
              className={`flex items-center gap-2 mb-3 text-lg font-bold ${t.heading}`}
            >
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full"
                style={{ background: type.color }}
              >
                <Icon size={16} color="#fff" strokeWidth={2.5} />
              </span>
              {type.pluralLabel}
              <span className={`text-sm font-normal ${t.muted}`}>({items.length})</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((point) => (
                <MapPointCard
                  key={point.id}
                  point={point}
                  locationsById={locationsById}
                  selected={point.id === selectedId}
                  onFocus={onFocus}
                  canManage={canManagePoint(user, point)}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  embed={embed}
                  t={t}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default MapPointList;
